/**
 * Smart search box.
 *  - instant local intents: WGS84 (decimal / DMS / comma-decimal), VN-2000 X/Y (either order, both
 *    projection zones), Plus Codes, Google Maps / OSM links (incl. short links), planning-project codes;
 *  - two geocoders queried in parallel (Esri World Geocoder suggest + Photon/OSM), rendered
 *    progressively as each answers, merged / de-duplicated / re-ranked with Vietnamese-aware fuzzy
 *    matching (accent-insensitive, abbreviation expansion, house-number agreement, proximity);
 *  - stale results from a cached prefix are shown while typing; responses cached (LRU);
 *  - coordinates of the top Esri suggestions are prefetched so Enter resolves instantly.
 */
import { $, esc, icon, debounce, scrollWithin, toast } from './ui.js';
import { getJSON, api, isAbort } from './api.js';
import { detectIntent, normCode, parseMapUrl } from './core/intent.js';
import { cleanQuery, rankCandidates, highlight, fold, tokens, similarity } from './core/text.js';
import { fromVN2000 } from './core/vn2000.js';
import { decode, isFull, recoverNearest } from './core/olc.js';
import { recent, addRecent, clearRecent } from './state.js';
import { savedList } from './savedstore.js';
import { ESRI_GEOCODE, PHOTON, HANOI_BBOX, HANOI_CENTER } from './config.js';
import { loadCatalog, loadLive, entries as catalogEntries, liveProject, pickLabel, titleOf, onMap, insidePoint, haystack, KIND } from './doan.js';

const els = {};
let pick = () => {};
let centerFn = () => HANOI_CENTER;
let items = [];
let active = -1;
let qseq = 0;
let ctl = null;
let pending = 0;
let pendingEnter = false;
let lastQuery = '';

/* ---------------- tiny LRU ---------------- */
class LRU {
  constructor(n) { this.n = n; this.m = new Map(); }
  get(k) { if (!this.m.has(k)) return undefined; const v = this.m.get(k); this.m.delete(k); this.m.set(k, v); return v; }
  set(k, v) { this.m.delete(k); this.m.set(k, v); if (this.m.size > this.n) this.m.delete(this.m.keys().next().value); }
  keys() { return [...this.m.keys()]; }
}
const cache = new LRU(120);       // `${provider}|${folded}` -> candidates
const coordCache = new Map();     // magicKey -> Promise<{lat,lng,bbox}>

/* ---------------- kinds & icons ---------------- */
const KIND_ICON = {
  house: 'house', street: 'route', poi: 'map-pin', school: 'school', uni: 'graduation-cap', hospital: 'hospital',
  market: 'store', admin: 'landmark', park: 'trees', worship: 'church', station: 'train-front', bus: 'bus',
  building: 'building', project: 'drafting-compass', coord: 'crosshair', vn2000: 'crosshair', pluscode: 'hash',
  link: 'link', recent: 'clock', saved: 'star', water: 'waves-horizontal',
};
const KIND_TAG = { project: 'Đồ án', decision: 'Chỉ có QĐ', coord: 'Toạ độ', vn2000: 'VN-2000', pluscode: 'Plus Code', link: 'Liên kết', recent: 'Gần đây', saved: 'Đã lưu' };

function kindFromText(label) {
  const f = fold(label);
  if (/^\d/.test(f)) return 'house';
  if (/^(duong|pho|ngo|ngach|hem|dai lo|quoc lo|cau)\b/.test(f)) return 'street';
  if (/\b(truong|mam non|tieu hoc|thcs|thpt)\b/.test(f)) return 'school';
  if (/\b(dai hoc|hoc vien|cao dang)\b/.test(f)) return 'uni';
  if (/\b(benh vien|phong kham|tram y te)\b/.test(f)) return 'hospital';
  if (/\b(cho|sieu thi|trung tam thuong mai|aeon|vincom|lotte)\b/.test(f)) return 'market';
  if (/^(phuong|xa|quan|huyen|thi xa|thi tran)\b/.test(f)) return 'admin';
  if (/\b(cong vien|vuon hoa)\b/.test(f)) return 'park';
  if (/\b(chua|den|nha tho|dinh)\b/.test(f)) return 'worship';
  if (/\b(ga|ben xe)\b/.test(f)) return 'station';
  return 'poi';
}
function kindFromOsm(key, value) {
  if (key === 'highway') return value === 'bus_stop' ? 'bus' : 'street';
  if (key === 'place') return /suburb|quarter|neighbourhood|city|town|village|hamlet|borough/.test(value) ? 'admin' : 'poi';
  if (key === 'boundary') return 'admin';
  if (key === 'amenity') {
    if (/school|kindergarten/.test(value)) return 'school';
    if (/university|college/.test(value)) return 'uni';
    if (/hospital|clinic|doctors|pharmacy/.test(value)) return 'hospital';
    if (/marketplace/.test(value)) return 'market';
    if (/place_of_worship/.test(value)) return 'worship';
    if (/townhall|police|courthouse/.test(value)) return 'admin';
    if (/bus_station/.test(value)) return 'bus';
  }
  if (key === 'shop') return 'market';
  if (key === 'leisure' && /park|garden/.test(value)) return 'park';
  if (key === 'railway') return 'station';
  if (key === 'building') return 'building';
  if (key === 'natural' && /water/.test(value)) return 'water';
  return 'poi';
}

/* ---------------- providers ---------------- */

async function esriSuggest(q, signal) {
  const c = centerFn();
  const url = `${ESRI_GEOCODE}/suggest?f=json&text=${encodeURIComponent(q)}&maxSuggestions=8&countryCode=VNM&location=${c.lng.toFixed(4)},${c.lat.toFixed(4)}&searchExtent=${HANOI_BBOX.join(',')}`;
  const j = await getJSON(url, { signal, track: false, timeout: 6000 });
  return (j?.suggestions || []).filter((s) => !s.isCollection).map((s, i) => {
    const text = s.text.replace(/,\s*VNM$/, '');
    const parts = text.split(', ');
    const label = parts.shift();
    return { label, sub: parts.filter((p) => !/^\d{5,6}$/.test(p)).join(', '), source: 'esri', rank: i, magicKey: s.magicKey, full: s.text, kind: kindFromText(label) };
  });
}

async function photonSearch(q, signal) {
  const c = centerFn();
  const url = `${PHOTON}/api/?q=${encodeURIComponent(q)}&limit=8&lang=default&lat=${c.lat.toFixed(4)}&lon=${c.lng.toFixed(4)}&bbox=${HANOI_BBOX.join(',')}`;
  const j = await getJSON(url, { signal, track: false, timeout: 6000 });
  return (j?.features || []).map((f, i) => {
    const p = f.properties || {};
    const [lng, lat] = f.geometry.coordinates;
    const street = p.street && p.housenumber ? `${p.housenumber} ${p.street}` : p.street;
    const label = p.name || street || p.district || p.city || 'Địa điểm';
    const sub = [p.name && street && street !== p.name ? street : null, p.locality, p.district, p.city !== p.district ? p.city : null]
      .filter((x, k, a) => x && a.indexOf(x) === k && x !== label).join(', ');
    const ex = p.extent; // [minLon, maxLat, maxLon, minLat]
    return {
      label, sub, source: 'photon', rank: i, lat, lng, kind: p.housenumber && !p.name ? 'house' : kindFromOsm(p.osm_key, p.osm_value),
      bbox: ex ? [Math.min(ex[0], ex[2]), Math.min(ex[1], ex[3]), Math.max(ex[0], ex[2]), Math.max(ex[1], ex[3])] : null,
    };
  });
}

function resolveEsri(c) {
  if (!c.magicKey) return Promise.reject(new Error('no key'));
  let p = coordCache.get(c.magicKey);
  if (!p) {
    const url = `${ESRI_GEOCODE}/findAddressCandidates?f=json&SingleLine=${encodeURIComponent(c.full)}&magicKey=${encodeURIComponent(c.magicKey)}&maxLocations=1&outFields=Addr_type,Type&outSR=4326`;
    p = getJSON(url, { track: false, timeout: 7000 }).then((j) => {
      const k = j?.candidates?.[0];
      if (!k) throw new Error('Không xác định được vị trí');
      const e = k.extent;
      return { lat: k.location.y, lng: k.location.x, bbox: e ? [e.xmin, e.ymin, e.xmax, e.ymax] : null, type: k.attributes?.Addr_type };
    });
    coordCache.set(c.magicKey, p);
    p.catch(() => coordCache.delete(c.magicKey));
  }
  return p;
}

/* ---------------- local sources ---------------- */

function localMatches(q) {
  const out = [];
  const qc = normCode(q);
  const qt = tokens(q);
  if (qc.length >= 2) {
    // project catalogue: digitised plans (corrected codes) and plans known only by their decision
    const qd = fold(q).replace(/\s+/g, '');
    const decQ = /^\d{2,5}\/qd/.test(qd) ? qd : '';
    for (const e of catalogEntries()) {
      haystack(e);
      const pc = e._code;
      let boost = 0;
      if (pc && pc === qc) boost = 0.55;
      else if (pc && pc.startsWith(qc) && /\d/.test(qc)) boost = 0.25;
      else if (decQ && e.dec && fold(e.dec).replace(/\s+/g, '').startsWith(decQ)) boost = 0.5;
      else {
        const sim = similarity(qt, tokens(e.short || titleOf(e)));
        if (sim > 0.62 && qt.length >= 2) boost = sim * 0.05; // name match: listed, but after places
      }
      if (!(boost > 0)) continue;
      const pt = insidePoint(e);
      if (!pt) continue;
      const p = liveProject(e);
      const label = pickLabel(e);
      const on = onMap(e);
      out.push({
        label, sub: on ? (e.name !== label ? e.name : KIND[e.kind]?.title || '') : ['Chưa số hoá', e.dec ? `QĐ ${e.dec}` : '', e.name !== label ? e.name : ''].filter(Boolean).join(' · '),
        source: 'local', rank: 0, boost, kind: 'project', lat: pt[1], lng: pt[0], bbox: p?.bbox || e.bbox, project: p, entry: e,
      });
    }
  }
  const fq = fold(q);
  if (fq.length >= 2) {
    savedList().forEach((p, i) => {
      if (fold(`${p.name} ${p.addr} ${p.plan?.code || ''}`).includes(fq)) out.push(savedItem(p, { source: 'local', rank: i, boost: 0.12 }));
    });
    recent().forEach((r, i) => {
      if (fold(r.label + ' ' + (r.sub || '')).includes(fq)) out.push({ ...r, source: 'local', rank: i, boost: 0.08, kind: 'recent', origKind: r.kind });
    });
  }
  return out;
}

const savedItem = (p, extra = {}) => ({
  label: p.name, sub: [p.plan?.fn, p.addr].filter(Boolean).join(' · '), lat: p.lat, lng: p.lng, kind: 'saved', zoom: 17.4, savedId: p.id, exact: true, ...extra,
});

function intentItems(intent) {
  const inBox = (lat, lng) => lng >= HANOI_BBOX[0] && lng <= HANOI_BBOX[2] && lat >= HANOI_BBOX[1] && lat <= HANOI_BBOX[3];
  switch (intent.type) {
    case 'latlng': {
      const { lat, lng } = intent;
      return [{ label: `${lat.toFixed(6)}, ${lng.toFixed(6)}`, sub: inBox(lat, lng) ? `Toạ độ WGS84 · vĩ độ, kinh độ${intent.format === 'dms' ? ' (độ phút giây)' : ''}` : 'Toạ độ nằm ngoài phạm vi Hà Nội', kind: 'coord', lat, lng, zoom: intent.zoom || 18, exact: true }];
    }
    case 'vn2000': {
      const a = fromVN2000(intent.x, intent.y, 'hn-105');
      const b = fromVN2000(intent.x, intent.y, 'utm-48');
      const xy = `X ${intent.x.toLocaleString('vi-VN', { maximumFractionDigits: 3 })} · Y ${intent.y.toLocaleString('vi-VN', { maximumFractionDigits: 3 })}`;
      return [
        { label: xy, sub: `VN-2000 KTT 105°00′ múi 3° (Hà Nội)${intent.swapped ? ' · đã đảo thứ tự X/Y' : ''}`, kind: 'vn2000', lat: a.lat, lng: a.lon, zoom: 19, exact: true },
        { label: xy, sub: `VN-2000 KTT 105° múi 6° (UTM)${intent.swapped ? ' · đã đảo thứ tự X/Y' : ''}`, kind: 'vn2000', lat: b.lat, lng: b.lon, zoom: 19, exact: true },
      ];
    }
    case 'pluscode': {
      let area = null;
      try {
        if (isFull(intent.code)) area = decode(intent.code);
        else {
          // short code: recover around the current view (if it is in Hanoi), else the city centre;
          // if that lands outside the city, try the neighbouring grid cells that fall inside it
          const c = centerFn();
          const ref = c && inBox(c.lat, c.lng) ? c : HANOI_CENTER;
          area = recoverNearest(intent.code, ref.lat, ref.lng);
          if (!inBox(area.lat, area.lng)) {
            const res = Math.pow(20, 2 - (8 - intent.code.indexOf('+')) / 2);
            const alt = [[0, res], [0, -res], [res, 0], [-res, 0]].map(([a, b]) => ({ ...area, lat: area.lat + a, lng: area.lng + b })).find((x) => inBox(x.lat, x.lng));
            if (alt) area = alt;
          }
        }
      } catch { return []; }
      if (!area) return [];
      const recovered = !isFull(intent.code);
      return [{ label: intent.code.toUpperCase(), sub: `Plus Code (Open Location Code)${recovered ? ' · khôi phục quanh Hà Nội' : ''}${inBox(area.lat, area.lng) ? '' : ' · nằm ngoài Hà Nội'}`, kind: 'pluscode', lat: area.lat, lng: area.lng, zoom: area.latRes < 0.0002 ? 19 : area.latRes < 0.003 ? 16 : 13, exact: true }];
    }
    case 'shortlink':
      return [{ label: 'Mở liên kết Google Maps', sub: intent.url, kind: 'link', url: intent.url, exact: true }];
    default:
      return [];
  }
}

/* ---------------- rendering ---------------- */

const optKey = (it) => `${it.kind}|${fold(it.label || '')}|${fold(it.sub || '')}`;
let shownKeys = new Set(); // rows already on screen: progressive updates do not re-animate them

function optHtml(it, i, q, keep = false) {
  const kind = it.kind === 'recent' ? 'recent' : it.kind;
  const tag = (it.entry && !onMap(it.entry) ? KIND_TAG.decision : KIND_TAG[kind]) || '';
  return `<li class="opt${keep ? ' keep' : ''}" role="option" id="opt-${i}" data-i="${i}" aria-selected="${i === active}" style="--i:${Math.min(i, 8)}">
    <span class="opt-ico">${icon(KIND_ICON[kind] || 'map-pin')}</span>
    <span class="opt-main"><span class="opt-title">${it.exact ? esc(it.label) : highlight(it.label, q, esc)}</span>${it.sub ? `<span class="opt-sub">${esc(it.sub)}</span>` : ''}</span>
    ${tag ? `<span class="opt-tag">${esc(tag)}</span>` : ''}
  </li>`;
}

function render(list, q, { loading = false, note = '' } = {}) {
  // groups follow the best match (an exact planning-code query such as "H1-4" lists projects
  // first); `items` is kept in visual order so arrow keys move top-to-bottom
  const places = list.filter((x) => x.kind !== 'project');
  const projs = list.filter((x) => x.kind === 'project');
  const groups = list[0]?.kind === 'project'
    ? [['Đồ án quy hoạch', projs], [places[0]?.exact ? 'Toạ độ' : 'Địa điểm', places]]
    : [[places[0]?.exact ? 'Toạ độ' : 'Địa điểm', places], ['Đồ án quy hoạch', projs]];
  items = groups.flatMap(([, g]) => g);
  if (active >= items.length) active = items.length - 1;
  if (active < 0 && items.length && q) active = 0;
  let html = '';
  let i = 0;
  const prev = shownKeys;
  shownKeys = new Set(items.map(optKey));
  for (const [title, g] of groups) {
    if (!g.length) continue;
    html += `<li class="search-group" role="presentation">${title}</li>`;
    for (const it of g) html += optHtml(it, i++, q, prev.has(optKey(it)));
  }
  if (!items.length && q) {
    html = loading
      ? `<li class="search-empty">${icon('loader-circle', 'spin')} Đang tìm…</li>`
      : `<li class="search-empty">${icon('search')} Không tìm thấy “${esc(q)}”. Thử thêm tên phường / quận, hoặc nhập toạ độ, VN-2000.</li>`;
  }
  els.list.innerHTML = html;
  els.foot.innerHTML = note || footNote();
  openDrop(true);
  syncActive();
}

function footNote() {
  return `<span class="keys"><kbd class="kbd">↑</kbd><kbd class="kbd">↓</kbd> chọn <kbd class="kbd">Enter</kbd> mở <kbd class="kbd">Esc</kbd> đóng</span><span>Esri · OpenStreetMap</span>`;
}

function renderEmpty() {
  shownKeys = new Set();
  const rec = recent();
  const saved = savedList();
  const sv = saved.slice(0, 4).map((p) => savedItem(p));
  let html = '';
  items = [...sv, ...rec.map((r) => ({ ...r, origKind: r.kind, kind: 'recent' }))];
  active = -1;
  if (sv.length) {
    html += `<li class="search-group" role="presentation" style="display:flex;justify-content:space-between;align-items:center">Đã lưu<button type="button" class="btn-link" data-open-saved>${saved.length > sv.length ? `Xem tất cả (${saved.length})` : 'Quản lý'}</button></li>`;
    html += sv.map((it, i) => optHtml(it, i, '')).join('');
  }
  if (rec.length) {
    html += `<li class="search-group" role="presentation" style="display:flex;justify-content:space-between;align-items:center">Tìm gần đây<button type="button" class="btn-link" data-clear-recent>Xoá</button></li>`;
    html += items.slice(sv.length).map((it, i) => optHtml(it, i + sv.length, '')).join('');
  }
  html += `<li class="search-group" role="presentation">Thử tìm theo</li>
    <li role="presentation"><div class="chips">
      <button type="button" class="chip" data-example="Hồ Hoàn Kiếm">${icon('map-pin')}Địa điểm</button>
      <button type="button" class="chip" data-example="21.02870, 105.85240">${icon('crosshair')}Toạ độ WGS84</button>
      <button type="button" class="chip" data-example="X 2326366.68 Y 588405.44">${icon('hash')}Toạ độ VN-2000</button>
      <button type="button" class="chip" data-example="H2-2">${icon('drafting-compass')}Mã phân khu</button>
      <button type="button" class="chip" data-example="https://www.google.com/maps/@21.0368,105.8346,17z">${icon('link')}Link Google Maps</button>
    </div></li>`;
  els.list.innerHTML = html;
  els.foot.innerHTML = `<span>Mẹo: chạm hoặc nhấp vào bất kỳ đâu trên bản đồ để xem quy hoạch</span><span class="keys"><kbd class="kbd">/</kbd></span>`;
  openDrop(true);
}

function openDrop(on) {
  els.drop.hidden = !on;
  els.box.setAttribute('aria-expanded', on ? 'true' : 'false');
  // lets small screens lift the search card above the control rail while the list is open
  document.body.classList.toggle('search-open', !!on);
}

function syncActive() {
  for (const li of els.list.querySelectorAll('.opt')) {
    const on = +li.dataset.i === active;
    li.setAttribute('aria-selected', on ? 'true' : 'false');
    if (on) scrollWithin(els.list, li, { margin: 4 });
  }
  if (active >= 0) els.input.setAttribute('aria-activedescendant', 'opt-' + active);
  else els.input.removeAttribute('aria-activedescendant');
}

function setLoading(on) { els.spinner.classList.toggle('on', on); }

/* ---------------- query pipeline ---------------- */

function stalePrefix(fq) {
  // best cached query that is a prefix of the current one (for instant feedback while typing)
  let best = null;
  for (const k of cache.keys()) {
    const [prov, key] = k.split('|');
    if (prov !== 'merged') continue;
    if (fq.startsWith(key) && (!best || key.length > best.length)) best = key;
  }
  return best ? cache.get('merged|' + best) : null;
}

function run(raw, immediate = false) {
  const q = raw.trim();
  lastQuery = q;
  ctl?.abort();
  ctl = null;
  const id = ++qseq;
  if (!q) { setLoading(false); pending = 0; renderEmpty(); return; }
  const intent = detectIntent(q);
  if (intent.type !== 'text') {
    setLoading(false); pending = 0;
    const its = intentItems(intent);
    active = its.length ? 0 : -1;
    render(its, q, { note: its.length ? '' : footNote() });
    if (pendingEnter && its.length) { pendingEnter = false; choose(0); }
    return;
  }
  const query = cleanQuery(intent.query);
  const fq = fold(query);
  const local = localMatches(query);
  const center = centerFn();
  const merged = cache.get('merged|' + fq);
  if (merged) {
    setLoading(false); pending = 0;
    render(rankCandidates(query, [...local, ...merged], { center }).slice(0, 9), q);
    if (pendingEnter) { pendingEnter = false; choose(Math.max(0, active)); }
    prefetch();
    return;
  }
  // instant feedback: local + stale prefix results re-ranked against the new query
  const stale = (stalePrefix(fq) || []).filter((c) => similarity(tokens(query), tokens(c.label + ' ' + (c.sub || ''))) > 0.35);
  render(rankCandidates(query, [...local, ...stale], { center }).slice(0, 9), q, { loading: true });
  if (fq.length < 2) { setLoading(false); return; }
  setLoading(true);
  const go = () => {
    if (id !== qseq) return;
    ctl = new AbortController();
    const signal = ctl.signal;
    const results = { esri: null, photon: null };
    pending = 2;
    const update = () => {
      if (id !== qseq) return;
      const all = [...(results.esri || []), ...(results.photon || [])];
      const ranked = rankCandidates(query, [...local, ...all], { center }).slice(0, 9);
      const done = pending === 0;
      if (done) {
        setLoading(false);
        cache.set('merged|' + fq, all);
      }
      // user already left the box (tapped the map…): keep the answer cached, do not pop the list up again
      if (document.activeElement !== els.input && !pendingEnter) return;
      render(ranked, q, { loading: !done });
      if (pendingEnter && (done || ranked.some((r) => Number.isFinite(r.lat)) && ranked.length)) { pendingEnter = false; choose(Math.max(0, active)); }
      if (done) prefetch();
    };
    const call = (name, fn) => {
      const ck = name + '|' + fq;
      const hit = cache.get(ck);
      const p = hit ? Promise.resolve(hit) : fn(query, signal).then((r) => { cache.set(ck, r); return r; });
      p.then((r) => { results[name] = r; }, (e) => { if (isAbort(e)) throw e; results[name] = []; })
        .then(() => { pending--; update(); }, () => {});
    };
    call('esri', esriSuggest);
    call('photon', photonSearch);
  };
  if (immediate) go(); else debouncedGo(go);
}
const debouncedGo = debounce((fn) => fn(), 110);

function prefetch() {
  // resolve coordinates of the top Esri suggestions in the background
  let n = 0;
  for (const it of items) {
    if (n >= 2) break;
    if (it.source === 'esri' && it.magicKey && !Number.isFinite(it.lat)) {
      n++;
      resolveEsri(it).then((r) => { it.lat = r.lat; it.lng = r.lng; it.bbox = it.bbox || r.bbox; it.addrType = r.type; }).catch(() => {});
    }
  }
}

/* ---------------- selection ---------------- */

async function choose(i) {
  const it = items[i];
  if (!it) return;
  // drop in-flight lookups so late provider answers cannot re-open the list over the result
  qseq++;
  ctl?.abort();
  ctl = null;
  debouncedGo.cancel();
  pending = 0;
  pendingEnter = false;
  setLoading(false);
  openDrop(false);
  els.input.blur();
  try {
    if (it.kind === 'link') {
      setLoading(true);
      const { url } = await api.resolve(it.url);
      setLoading(false);
      const r = url ? parseMapUrl(url) : null;
      if (r && r.type === 'latlng') return finish({ label: `${r.lat.toFixed(6)}, ${r.lng.toFixed(6)}`, sub: 'Từ liên kết Google Maps', lat: r.lat, lng: r.lng, kind: 'coord', zoom: r.zoom || 18 });
      if (r && r.type === 'text') { els.input.value = r.query; els.input.focus(); run(r.query, true); return; }
      throw new Error('Không đọc được toạ độ từ liên kết');
    }
    let { lat, lng, bbox } = it;
    if (!Number.isFinite(lat) && it.magicKey) {
      setLoading(true);
      const r = await resolveEsri(it);
      setLoading(false);
      ({ lat, lng } = r);
      bbox = bbox || r.bbox;
      it.addrType = r.type;
    }
    if (!Number.isFinite(lat)) throw new Error('Không xác định được vị trí');
    const kind = it.kind === 'recent' ? it.origKind : it.kind;
    // pick a sensible zoom: precise points close in, areas / streets fit their extent
    let zoom = it.zoom;
    const span = bbox ? Math.max((bbox[2] - bbox[0]) * 103900, (bbox[3] - bbox[1]) * 110600) : 0;
    const fit = (kind === 'project' || kind === 'admin' || kind === 'street') && span > 250 ? bbox : null;
    if (!zoom) zoom = kind === 'house' || it.addrType === 'PointAddress' ? 18.3 : kind === 'street' ? 16.8 : 17.4;
    finish({ label: it.label, sub: it.sub, lat, lng, kind, zoom, bbox: fit, project: it.project, entry: it.entry });
  } catch (e) {
    setLoading(false);
    toast(e.message || 'Không mở được kết quả', { type: 'err' });
  }
}

function finish(r) {
  els.input.value = r.label;
  toggleClear();
  // decision-only plans have no point to come back to: the catalogue card is the result
  if (r.kind !== 'coord' && r.kind !== 'vn2000' && r.kind !== 'saved' && !(r.entry && !onMap(r.entry))) addRecent(r);
  pick(r);
}

/* ---------------- wiring ---------------- */

function toggleClear() {
  const has = !!els.input.value;
  els.clear.hidden = !has;
  els.kbd.hidden = has || document.activeElement === els.input;
}

let openSavedFn = null;
export function initSearch({ onPick, getCenter, openSaved }) {
  pick = onPick;
  openSavedFn = openSaved || null;
  centerFn = getCenter || centerFn;
  els.box = $('#search');
  els.input = $('#search-input');
  els.drop = $('#search-drop');
  els.list = $('#search-list');
  els.foot = $('#search-foot');
  els.spinner = $('#search-spinner');
  els.clear = $('#search-clear');
  els.kbd = $('#search-kbd');

  const rerun = () => { if (document.activeElement === els.input && els.input.value.trim()) run(els.input.value, true); };
  // the catalogue is small and static; the live list of digitised projects (~1 MB upstream) only
  // adds geometry and plans published after the catalogue was built: fetch it once search is used
  let liveAsked = false;
  const wantLive = () => { if (liveAsked) return; liveAsked = true; loadLive().then(rerun, () => { liveAsked = false; }); };
  loadCatalog().then(rerun, () => {});
  els.input.addEventListener('focus', wantLive);
  els.input.addEventListener('pointerdown', wantLive, { passive: true });

  els.input.addEventListener('input', () => { toggleClear(); active = -1; run(els.input.value); });
  els.input.addEventListener('focus', () => {
    toggleClear();
    if (!els.input.value.trim()) renderEmpty();
    else run(els.input.value, true); // instant when cached; refreshes answers that arrived while unfocused
  });
  els.input.addEventListener('blur', () => { setTimeout(() => { if (!els.box.contains(document.activeElement) && !els.drop.contains(document.activeElement)) openDrop(false); toggleClear(); }, 150); });
  els.input.addEventListener('keydown', (e) => {
    const opts = items.length;
    if (e.key === 'ArrowDown') { e.preventDefault(); if (els.drop.hidden) openDrop(true); if (opts) { active = (active + 1) % opts; syncActive(); } }
    else if (e.key === 'ArrowUp') { e.preventDefault(); if (opts) { active = (active - 1 + opts) % opts; syncActive(); } }
    else if (e.key === 'Enter') {
      e.preventDefault();
      if (e.isComposing) return;
      const q = els.input.value.trim();
      if (!q) return;
      if (opts && !els.drop.hidden && (active >= 0 || pending === 0) && (lastQuery === q)) choose(Math.max(0, active));
      else { pendingEnter = true; debouncedGo.cancel(); run(q, true); }
    } else if (e.key === 'Escape') {
      if (!els.drop.hidden) { openDrop(false); e.stopPropagation(); }
      else if (els.input.value) { els.input.value = ''; toggleClear(); e.stopPropagation(); }
      else els.input.blur();
    }
  });
  // keep focus in the input when pressing anywhere inside the list (rows, group titles, chips)
  els.list.addEventListener('pointerdown', (e) => { if (e.target.closest('li')) e.preventDefault(); });
  els.list.addEventListener('click', (e) => {
    const opt = e.target.closest('.opt');
    if (opt) return choose(+opt.dataset.i);
    const chip = e.target.closest('[data-example]');
    if (chip) { els.input.value = chip.dataset.example; els.input.focus(); toggleClear(); run(chip.dataset.example, true); return; }
    if (e.target.closest('[data-clear-recent]')) { clearRecent(); renderEmpty(); }
    if (e.target.closest('[data-open-saved]')) { openDrop(false); els.input.blur(); openSavedFn?.(); }
  });
  els.list.addEventListener('mousemove', (e) => {
    const opt = e.target.closest('.opt');
    if (opt && +opt.dataset.i !== active) { active = +opt.dataset.i; syncActive(); }
  });
  els.clear.addEventListener('click', () => { els.input.value = ''; toggleClear(); els.input.focus(); renderEmpty(); });
  document.addEventListener('pointerdown', (e) => { if (!$('#search-card').contains(e.target)) openDrop(false); });
  toggleClear();
}

export function focusSearch() { els.input.focus(); els.input.select(); }
export function setSearchText(t) { els.input.value = t || ''; toggleClear(); }
